import { useEffect, useRef } from 'react';

/**
 * The trigger for an infinite-scroll list (the doctor's سوابق ویزیت, PM-0533).
 *
 * Place it right after the last row. While `active`, `onVisible` fires whenever the
 * sentinel is within `rootMargin` of the viewport — a screenful early, so the next rows
 * are usually there before the doctor reaches the bottom.
 *
 * It RE-OBSERVES whenever `watch` changes (pass the number of rows on screen). An
 * IntersectionObserver reports only on a change, so after a page lands that still leaves
 * the sentinel in view — a tall monitor, a short page — nothing would ever fire again
 * and the list would stall half-empty. Re-observing replays the initial report.
 *
 * The scroll container is whichever ancestor scrolls (root = null still honours a
 * nested scroller's clipping), so the same component works inside the shell's main
 * pane. Where IntersectionObserver is missing it renders the marker and does nothing;
 * the screen shows a «نمایش بیشتر» button for that case (see `canObserve`).
 */
export const canObserve = () => typeof IntersectionObserver !== 'undefined';

export default function InfiniteSentinel({
  active, onVisible, watch, rootMargin = '480px 0px', testId = 'infinite-sentinel',
}) {
  const ref = useRef(null);
  const callback = useRef(onVisible);
  callback.current = onVisible;

  useEffect(() => {
    const node = ref.current;
    if (!active || !node || !canObserve()) return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries.some(e => e.isIntersecting)) callback.current(); },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [active, watch, rootMargin]);

  return <div ref={ref} data-testid={testId} aria-hidden="true" style={{ height: 1 }} />;
}
